import {
  assignmentListSchema,
  assignmentResultSchema,
  assignmentSchema,
  bulkAssignResultSchema,
  myWorkItemSchema,
  myWorkListSchema,
  workActionResultSchema,
  workloadListSchema,
  type Assignment,
  type AssignmentList,
  type AssignmentResult,
  type BulkAssignInput,
  type BulkAssignResult,
  type CreateAssignmentInput,
  type FlagAssignmentProblemInput,
  type MyWorkFilter,
  type MyWorkItem,
  type MyWorkList,
  type OverrideAssignmentRateInput,
  type ReassignInput,
  type WorkActionResult,
  type WorkloadList,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * The two assignment surfaces, kept as two clients against two paths.
 *
 * `/assignments` is the owner's and carries rates. `/my-work` is the karigar's and carries none.
 * The split is the API's, not this file's invention — a tailor calling anything in the first
 * group gets a 403 pointing them at the second, which is why nothing here tries to be clever
 * about serving one screen from the other's endpoint.
 */

const ASSIGNMENTS = '/assignments';
const MY_WORK = '/my-work';

export interface AssignmentFilters {
  itemId?: string;
  orderId?: string;
  assignedToUserId?: string;
  openOnly?: boolean;
}

export async function listAssignments(
  client: ApiClient,
  filters: AssignmentFilters = {},
): Promise<AssignmentList> {
  const query: Record<string, string> = {};
  if (filters.itemId) query.itemId = filters.itemId;
  if (filters.orderId) query.orderId = filters.orderId;
  if (filters.assignedToUserId) query.assignedToUserId = filters.assignedToUserId;
  if (filters.openOnly) query.openOnly = 'true';
  return await client.get(ASSIGNMENTS, { schema: assignmentListSchema, query });
}

/** Every job somebody has stopped on. The owner's alert list. */
export async function listOpenProblems(client: ApiClient): Promise<AssignmentList> {
  return await client.get(`${ASSIGNMENTS}/problems`, { schema: assignmentListSchema });
}

export async function getWorkload(client: ApiClient): Promise<WorkloadList> {
  return await client.get(`${ASSIGNMENTS}/workload`, { schema: workloadListSchema });
}

export async function createAssignment(
  client: ApiClient,
  input: CreateAssignmentInput,
): Promise<AssignmentResult> {
  return await client.post(ASSIGNMENTS, input, { schema: assignmentResultSchema });
}

export async function bulkAssign(
  client: ApiClient,
  input: BulkAssignInput,
): Promise<BulkAssignResult> {
  return await client.post(`${ASSIGNMENTS}/bulk`, input, { schema: bulkAssignResultSchema });
}

export async function reassign(
  client: ApiClient,
  assignmentId: string,
  input: ReassignInput,
): Promise<AssignmentResult> {
  return await client.post(`${ASSIGNMENTS}/${assignmentId}/reassign`, input, {
    schema: assignmentResultSchema,
  });
}

export async function overrideAssignmentRate(
  client: ApiClient,
  assignmentId: string,
  input: OverrideAssignmentRateInput,
): Promise<Assignment> {
  return await client.put(`${ASSIGNMENTS}/${assignmentId}/rate`, input, {
    schema: assignmentSchema,
  });
}

export async function resolveAssignmentProblem(
  client: ApiClient,
  assignmentId: string,
  note?: string,
): Promise<Assignment> {
  return await client.post(`${ASSIGNMENTS}/${assignmentId}/problem/resolve`, note ? { note } : {}, {
    schema: assignmentSchema,
  });
}

export async function cancelAssignment(
  client: ApiClient,
  assignmentId: string,
): Promise<Assignment> {
  return await client.del(`${ASSIGNMENTS}/${assignmentId}`, undefined, {
    schema: assignmentSchema,
  });
}

// --- The karigar's own screen ------------------------------------------------------------------

export async function getMyWork(client: ApiClient, filter: MyWorkFilter): Promise<MyWorkList> {
  return await client.get(MY_WORK, { schema: myWorkListSchema, query: { filter } });
}

export async function startJob(client: ApiClient, assignmentId: string): Promise<WorkActionResult> {
  return await client.post(
    `${MY_WORK}/${assignmentId}/start`,
    {},
    {
      schema: workActionResultSchema,
    },
  );
}

export async function completeJob(
  client: ApiClient,
  assignmentId: string,
): Promise<WorkActionResult> {
  return await client.post(
    `${MY_WORK}/${assignmentId}/complete`,
    {},
    {
      schema: workActionResultSchema,
    },
  );
}

export async function flagJobProblem(
  client: ApiClient,
  assignmentId: string,
  input: FlagAssignmentProblemInput,
): Promise<MyWorkItem> {
  return await client.post(`${MY_WORK}/${assignmentId}/problem`, input, {
    schema: myWorkItemSchema,
  });
}
